import React, {useEffect, useState} from 'react';
import {
  ActivityIndicator,
  FlatList,
  Linking,
  Platform,
  ScrollView,
  StyleSheet,
  View,
} from 'react-native';
import Toast from 'react-native-toast-message';
import {observer} from 'mobx-react';

import GamePlayActions from '../../components/activation/games/GamePlayActions';
import FeedProductTileCard from '../../components/FeedProductTileCard';
import LoadingImg from '../../components/LoadingImg';
import PromotionUserSignInModal from '../../components/modals/PromotionUserSignInModal';
import OpenURLButton from '../../components/OpenURLButton';
import PromotionCaptureDetails, {
  PromotionUserSubmission,
} from '../../components/PromotionCaptureDetails';
import {ERROR_MESSAGE} from '../../components/ToastBottom';
import Button from '../../components/ui-controls/Button';
import ContentView from '../../components/ui-controls/layout/ContentView';
import Markdown from '../../components/ui-controls/Markdown';
import Text from '../../components/ui-controls/Text';
import useIsLoggedIn from '../../hooks/useIsLoggedIn';
import {useIsSelectedStoreLoyaltyEnabled} from '../../hooks/useSelectedStore';
import {useWebScreenDimensions} from '../../hooks/useWebScreenDimensions';
import {AirtablePromotionItem} from '../../models/airtable';
import {AppRoute} from '../../models/AppRoute';
import {CreateCompetitionEntryResponse} from '../../models/middleware';
import {RouteNavigatorScreenProps} from '../../models/navigationTypes';
import {
  isDrawnWinnersPromotion,
  isEcommerceOnlyPromotion,
  isInStoreOnlyPromotion,
} from '../../stores/promotionsStore';
import {useStores} from '../../stores/use-stores';
import theme from '../../style/theme';
import apiService from '../../transport/apiService';
import {OrderLine} from '../../transport/apiServiceResponse';
import {Analytics} from '../../utils/analytics/analytics';
import {EventName} from '../../utils/analytics/events';
import {getTermsAndConditionsUrl} from '../../utils/competitions';
import {GraphicContentType} from '../../utils/feedAndDiscover/constants';
import {getFeedDiscoverDetailGraphics} from '../../utils/feedAndDiscover/media';
import {formatDate} from '../../utils/functions';
import {isGiftCardsPromotion} from '../../utils/giftCards';
import {getHeroImageDetail} from '../../utils/heroImage';
import {getEnterHereButtonText} from '../../utils/promotions';

import GiftCardPromotionExtraContent from './GiftCardPromotionExtraContent';

type SuccessViewProps = {
  activation: AirtablePromotionItem;
  navigation: PromotionDetailScreenProps['navigation'];
  createCompetitionEntryResponse?: CreateCompetitionEntryResponse;
  onNotEnterGame?: () => void;
};
const SuccessView = ({
  activation: promotion,
  navigation,
  createCompetitionEntryResponse,
  onNotEnterGame = () => {},
}: SuccessViewProps) => {
  const styles = StyleSheet.create({
    container: {
      justifyContent: 'space-around',
      alignItems: 'center',
      height: 150,
      marginTop: 10,
      paddingVertical: 10,
      backgroundColor: '#c5edc5',
    },
  });

  const {adhocPromotionMessage, endDate} = promotion;
  const summerCashGrabConfig =
    createCompetitionEntryResponse?.meta?.game?.SUMMER_CASH_GRAB;

  const copy = {
    default: {
      title: 'GOOD LUCK!',
      body: ['YOU ARE IN THE DRAW', 'WINNERS WILL BE ANNOUNCED', 'BY EMAIL'],
    },
    game: {
      title: 'GOOD LUCK!',
      body: ['THANK YOU FOR PLAYING'],
    },
  };

  const {title, body} = promotion.game ? copy.game : copy.default;

  return (
    <>
      <View style={styles.container}>
        {adhocPromotionMessage ? (
          // On web (react-native-web) styles are ignored #44
          // https://github.com/iamacup/react-native-markdown-display/issues/44
          // This was supposedly fixed in 5.1.0-rc1 but it's still an issue
          // Otherwise, only the <Markdown> styles would do
          <View style={{textAlign: 'center'}}>
            <Markdown>{adhocPromotionMessage}</Markdown>
          </View>
        ) : (
          <>
            <Text style={{fontWeight: 'bold'}}>{title}</Text>
            {body.map((line, index) => (
              <Text key={`${line}${index}`}>{line}</Text>
            ))}
          </>
        )}
        <Text>{`ENTRIES CLOSE ${formatDate(endDate)}`}</Text>
      </View>
      {!!promotion.game && (
        <GamePlayActions
          activation={promotion}
          navigation={navigation}
          gameParams={summerCashGrabConfig}
          onNotEnterGame={onNotEnterGame}
        />
      )}
    </>
  );
};

const DrawnWinnersView = ({promotion}: {promotion: AirtablePromotionItem}) => {
  const {isWebDesktopScreen} = useWebScreenDimensions();

  const heroImage = getHeroImageDetail(promotion, isWebDesktopScreen);

  return (
    <ContentView testID="promotion-detail-screen">
      <ScrollView
        showsVerticalScrollIndicator={false}
        showsHorizontalScrollIndicator={false}>
        {getFeedDiscoverDetailGraphics(
          heroImage,
          null,
          null,
          GraphicContentType.Image,
        )}
        <View style={styles.textCard}>
          <Text style={styles.headline}>{promotion?.headline}</Text>
          <Text style={styles.copy}>{promotion?.description}</Text>
        </View>
        <View style={styles.textCard}>
          <Text style={styles.headline}>WINNERS</Text>
          <Text style={styles.copy}>{promotion?.winners}</Text>
        </View>
      </ScrollView>
    </ContentView>
  );
};

const LoadingView = () => {
  return (
    <ContentView>
      <LoadingImg />
    </ContentView>
  );
};

export type PromotionDetailScreenProps =
  RouteNavigatorScreenProps<AppRoute.PROMOTION> & {
    onOnlineOrderAfterSubmit?: () => void;
    onOnlineOrderCancel?: () => void;
    isLastCompetitionRemaining: boolean; // Summer Cash Grab
  };

type PromotionDetailScreenStandaloneProps = {
  promotion?: AirtablePromotionItem;
  orderNumber?: string;
  onOnlineOrderAfterSubmit?: () => void;
  onOnlineOrderCancel?: () => void;
  isLastCompetitionRemaining?: boolean; // Summer Cash Grab
  navigation: any;
  isRootStoreSelectionFlow?: boolean;
  extraContent?: React.ReactNode;
};
export const PromotionDetailScreenStandalone = ({
  promotion,
  orderNumber,
  onOnlineOrderAfterSubmit = () => {},
  onOnlineOrderCancel = () => {},
  isLastCompetitionRemaining = false,
  navigation,
  isRootStoreSelectionFlow = false,
  extraContent,
}: PromotionDetailScreenStandaloneProps) => {
  const isOnlineOrder = !!orderNumber;

  const {promotionsStore, cartStore, shopSelectionStore} = useStores();
  const isLoggedIn = useIsLoggedIn();
  const isSelectedStoreLoyaltyEnabled = useIsSelectedStoreLoyaltyEnabled();

  const [isLoading, setLoading] = useState(false);
  const [linkedProducts, setLinkedProducts] = useState<OrderLine[]>([]);
  const [isLoadingLinkedProducts, setIsLoadingLinkedProducts] = useState(false);

  const [isSignInModalVisible, setIsSignInModalVisible] = useState(false);
  const [isCapturingDetails, setIsCapturingDetails] = useState(false);
  const [isSubmittingEntry, setIsSubmittingEntry] = useState(false);
  const [isSubmittedEntry, setIsSubmittedEntry] = useState(false);
  const [submissionResponse, setSubmissionResponse] =
    useState<CreateCompetitionEntryResponse>();

  const [shouldDisplayDescription, setShouldDisplayDescription] = useState(
    isOnlineOrder ? false : !isCapturingDetails,
  );

  const [isReadyToCaptureDetails, setIsReadyToCaptureDetails] = useState(
    // isOnlineOrder && !promotion?.game ? true : isCapturingDetails,
    isOnlineOrder ? true : isCapturingDetails,
  );

  const {isWebDesktopScreen} = useWebScreenDimensions();

  //loading linked products for the promotions
  useEffect(() => {
    if (promotion) {
      if (shopSelectionStore.selectedStore) {
        (async function () {
          setLinkedProducts([]);
          setIsLoadingLinkedProducts(true);
          const linked = await promotionsStore.loadLinkedProducts(promotion);
          setLinkedProducts(linked as OrderLine[]);
          setIsLoadingLinkedProducts(false);
        })();
      }
    }
  }, [promotionsStore, promotion, shopSelectionStore.selectedStore]);

  useEffect(() => {
    if (promotion) {
      Analytics.logEvent({
        eventName: EventName.ViewActivation,
        parameters: {
          promotion_name: promotion.activationUrlName,
          competition_id: promotion.id,
          order_number: orderNumber || '',
        },
      });
    }
    // only once
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!promotion) {
    return (
      <View testID="promotion-detail-screen-promotion-not-found">
        <Text>Promotion not found</Text>
      </View>
    );
  }

  const showMemberOnlyPromo = promotion.membersOnly ? isSelectedStoreLoyaltyEnabled && isLoggedIn : true;
  if (!showMemberOnlyPromo) {
    return <View testID="promotion-detail-screen-promotion-not-found-member-only">
      <Text>Promotion not found, please check that you are logged in and are a store loyalty member</Text>
    </View>
  }

  const showErrorToast = (message: string) =>
    Toast.show({
      type: ERROR_MESSAGE,
      text1: message,
    });

  async function handleSubmit(data: PromotionUserSubmission) {
    if (!promotion) {
      console.error('Error submitting competition entry: No promotion');
      showErrorToast(
        'There was an issue receiving your entry, please try again',
      );
      return;
    }

    setIsSubmittingEntry(true);

    try {
      const response = await apiService.createCompetitionEntry({
        ...data,
        // ⚠️ Beware: We had an issue where the backend was receiving the same value for
        // `orderNumber` and `orderId`. Before, we were using `orderId` as `route.params.orderId`.
        // The change done that seems to get it fixed is:
        // https://dev.azure.com/lmgdevops/branded_website/_git/branded_website/commit/072681e058a407554c207a47c647c9dfc67a7033?refName=refs/heads/main&path=/frontend/app/components/AddressPicker.js
        // Which is just the line below 🤷🏽‍♂️
        // Also, only send orderId if we have orderNumber
        orderId:
          orderNumber && cartStore.currentOrder
            ? cartStore.currentOrder.id
            : undefined,
        orderNumber,
        competitionId: promotion.id,
        promotionName: promotion.activationUrlName,
        selectedStoreName: shopSelectionStore?.selectedStore?.name || '',
        thirdPartyId: promotion.mflId,
      });
      setSubmissionResponse(response);
      setIsSubmittedEntry(true);
      if (!promotion.game) {
        onOnlineOrderAfterSubmit();
      }
    } catch (err: any) {
      // TODO: We need a global way of detecting 401s from MFL and notifying the user
      // https://dev.azure.com/lmgdevops/branded_website/_workitems/edit/1750
      if (err.message !== 'Unauthorized') {
        console.error('Error submitting competition entry:', err);
        showErrorToast(
          'There was an issue receiving your entry, please try again',
        );
      }
      onOnlineOrderCancel();
    }
    setIsSubmittingEntry(false);
  }

  async function handleSubmitForGame() {
    setIsSubmittingEntry(true);

    try {
      const response = await apiService.createGameCompetitionEntry();
      setSubmissionResponse(response);
      setIsSubmittedEntry(true);
      navigation.navigate(AppRoute.SUMMER_CASH_GRAB_GAME, {
        url: promotion?.iFrameLink,
        iFrameLinkParams:
          response.meta?.game?.SUMMER_CASH_GRAB?.iFrameLinkParams,
      });
    } catch (err: any) {
      // TODO: We need a global way of detecting 401s from MFL and notifying the user
      // https://dev.azure.com/lmgdevops/branded_website/_workitems/edit/1750
      if (err.message !== 'Unauthorized') {
        console.error('Error submitting competition entry:', err);
        showErrorToast(
          'There was an issue receiving your entry, please try again',
        );
      }
      onOnlineOrderCancel();
    }
    setIsSubmittingEntry(false);
  }

  // This is only visible for entries that are part of the post
  // order flow. Need to give the user some way to skip entering.
  const handleOnlineOrderCancel = onOnlineOrderCancel;

  const tryGetWebWindowReference = () => {
    // Hotfix: Safari hack
    // When `promotionItem.offersStore`, Safari will block the "popup" window
    // given the async nature of the `transferSession` call and the fact that in
    // our current version of `react-native-web` we can't specify a target for
    // `Linking.openURL`. react-native-web now supports "Allow explicit target
    // for Linking.openURL(), default to "_blank" but when upgrading to 0.19.x
    // I got stuff like:
    // "Can't resolve 'react-native-web/dist/exports/PermissionsAndroid'..."
    // (which is effectively removed: https://github.com/necolas/react-native-web/compare/0.18.12...0.19.0)
    // References:
    // - 2093-[TechDebt]Upgrade react-native-web to 0.19.x: https://dev.azure.com/lmgdevops/branded_website/_workitems/edit/2093)
    // - Allow explicit target for Linking.openURL(), default to "_blank" #2277: https://github.com/necolas/react-native-web/pull/2277
    if (Platform.OS === 'web') {
      try {
        return window.open('about:blank', '_blank');
      } catch (error) {
        console.error('Error', error);
      }
    }
  };

  const handleExternalMicrosite = async () => {
    let params = '';
    let webWindowReference;

    if (promotion.offersStore) {
      webWindowReference = tryGetWebWindowReference();
      setLoading(true);
      const transferTokens = await apiService.transferSession();
      if (transferTokens) {
        params = `${transferTokens.parameter}=${transferTokens.token}`;
      }
      setLoading(false);
    }

    let url = promotion.externalMicrosite.trim();
    if (!url.startsWith('https://')) url = `https://${url}`;
    if (params) {
      if (url.includes('?')) url = `${url}&${params}`;
      else url = `${url}?${params}`;
    }

    if (webWindowReference) {
      // Safari hack
      webWindowReference.location = url;
    } else {
      await Linking.openURL(url);
    }
  };

  const handleIframeLink = () => {
    if (promotion.game) {
      navigation.navigate(AppRoute.SUMMER_CASH_GRAB_GAME, {
        url: promotion.iFrameLink,
      });
      return;
    }

    navigation.navigate(AppRoute.PROMOTION_IFRAME, {
      url: promotion.iFrameLink,
    });
  };

  const handleShoppedInStorePress = async () => {
    if (promotion.externalMicrosite) {
      await handleExternalMicrosite();
      return;
    }

    if (promotion.iFrameLink) {
      handleIframeLink();
      return;
    }

    if (!isLoggedIn) {
      setIsSignInModalVisible(true);
    } else {
      setIsCapturingDetails(true);
      setShouldDisplayDescription(false);
      setIsReadyToCaptureDetails(true);
    }
  };

  const handleNotEnterGame = isOnlineOrder
    ? handleOnlineOrderCancel
    : navigation.goBack;

  const heroImage = getHeroImageDetail(promotion, isWebDesktopScreen);
  const termsAndConditionsUrl = getTermsAndConditionsUrl(promotion);

  const isEcommerceOnly = isEcommerceOnlyPromotion(promotion);
  const isInStoreOnly = isInStoreOnlyPromotion(promotion);
  const isEnterHereButtonVisible =
    !promotion.noEntryPromo 
    && !isEcommerceOnly 
    && isInStoreOnly
    && showMemberOnlyPromo;
  const isReceiptRequired = !promotion.noReceiptRequired;

  const isDrawnWinners = isDrawnWinnersPromotion(promotion);
  if (isDrawnWinners) {
    return <DrawnWinnersView promotion={promotion} />;
  }

  if (isLoading || !promotion || isSubmittingEntry) {
    return <LoadingView />;
  }

  return (
    <ContentView testID="promotion-detail-screen">
      <ScrollView
        showsVerticalScrollIndicator={false}
        showsHorizontalScrollIndicator={false}>
        {getFeedDiscoverDetailGraphics(heroImage, null, null, null)}
        <View style={styles.textCard}>
          <Text style={styles.headline}>{promotion?.headline}</Text>
          {!!shouldDisplayDescription && (
            <Markdown>{promotion?.description}</Markdown>
          )}
        </View>
        {isSubmittedEntry ? (
          <SuccessView
            activation={promotion}
            navigation={navigation}
            createCompetitionEntryResponse={submissionResponse}
            onNotEnterGame={handleNotEnterGame}
          />
        ) : isReadyToCaptureDetails ? (
          <PromotionCaptureDetails
            activation={promotion}
            onSubmit={promotion.game ? handleSubmitForGame : handleSubmit}
            orderNumber={orderNumber}
            onCancel={handleOnlineOrderCancel}
            isReceiptRequired={isReceiptRequired}
            isLastCompetitionRemaining={isLastCompetitionRemaining}
          />
        ) : (
          <>
            {/* {!!promotion.game && (
              <GamePlayActions
                activation={promotion}
                navigation={navigation}
                isPracticePlay
                onNotEnterGame={navigation.goBack}
              />
            )} */}
            {!!isEnterHereButtonVisible && (
              <Button
                onPress={handleShoppedInStorePress}
                title={getEnterHereButtonText(promotion)}
                buttonStyle={styles.enterHereButton}
              />
            )}
            {!!termsAndConditionsUrl && (
              <View style={styles.termsAndConditions}>
                <OpenURLButton
                  url={termsAndConditionsUrl}
                  textStyle={styles.termsAndConditionsLink}>
                  Terms and conditions
                </OpenURLButton>
              </View>
            )}
            {isGiftCardsPromotion(promotion) && (
              <GiftCardPromotionExtraContent
                navigation={navigation}
                isRootStoreSelectionFlow={isRootStoreSelectionFlow}
              />
            )}
            {!!extraContent && extraContent}
            {promotion?.barcodes?.length > 0 &&
              (isLoadingLinkedProducts ? (
                <ContentView>
                  <ActivityIndicator />
                </ContentView>
              ) : (
                <FlatList
                  data={linkedProducts}
                  style={styles.flatList}
                  renderItem={({item: product}) => (
                    <FeedProductTileCard key={product.id} product={product} />
                  )}
                  horizontal
                />
              ))}
          </>
        )}
        <PromotionUserSignInModal
          isVisible={isSignInModalVisible}
          onNavigate={() => {
            setIsSignInModalVisible(false);
            navigation.navigate(AppRoute.ACCOUNT_NOT_LOGGED_IN);
          }}
        />
      </ScrollView>
    </ContentView>
  );
};

const PromotionDetailScreen = observer(
  ({
    route,
    navigation,
    onOnlineOrderAfterSubmit = () => {},
    onOnlineOrderCancel = () => {},
    isLastCompetitionRemaining = true,
  }: PromotionDetailScreenProps) => {
    const promotion = route.params?.promotion;
    const orderNumber = route.params?.orderNumber;

    // Handle deep linking "404". TODO: Better way? In "linking.ts"?
    useEffect(() => {
      if (!route.params?.promotion) navigation.navigate(AppRoute.HOME);
    }, [navigation, route.params]);

    return (
      <PromotionDetailScreenStandalone
        promotion={promotion}
        orderNumber={orderNumber}
        onOnlineOrderAfterSubmit={onOnlineOrderAfterSubmit}
        onOnlineOrderCancel={onOnlineOrderCancel}
        isLastCompetitionRemaining={isLastCompetitionRemaining}
        navigation={navigation}
      />
    );
  },
);

export default PromotionDetailScreen;

const styles = StyleSheet.create({
  textCard: {
    marginTop: 24,
  },
  termsAndConditions: {
    marginTop: 16,
    alignItems: 'center',
  },
  termsAndConditionsLink: {
    fontSize: 14,
    color: theme.colour.text.link.primary,
  },
  enterText: {
    fontSize: 16,
    color: 'white',
  },
  headline: {
    fontSize: 20,
    color: theme.colour.text.heading.primary,
    fontWeight: 'bold',
    marginBottom: 8,
  },
  flatList: {
    marginTop: 32,
    marginBottom: 4,
  },
  enterHereButton: {
    marginTop: 16,
    width: '80%',
  },
  copy: {
    fontSize: 16,
  },
  linkedProductsActivityIndicator: {
    flex: 1,
  },
});
